<template>
  <a-dialog
      ref="dialog"
      :persistent="false"
      size="large"
      name="manage_notes"
      @close="close">
    <span slot="title">
      {{ $t('manage') }}
    </span>
    <div slot="content" class="mt-4">
      <a-action-buttons-bar
          class="mb-4"
          :options="{ selected: table.selected}"
          :buttons="buttons">
      </a-action-buttons-bar>

      <v-expand-transition>
        <v-form ref="form" v-model="create.valid" v-show="create.opened" class="mb-4">
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-textarea
                  filled
                  :prepend-icon="$vuetify.breakpoint.smAndUp ? 'mdi-text' : ''"
                  rows="3"
                  v-model="create.fields.description"
                  :label="$t('description')"
                  persistent-hint
                  :hint="$t('hint-description')"
                  :rules="create.rules.description"
                  :counter="1000"
                  auto-grow
              ></v-textarea>
            </v-col>
            <v-col cols="12" sm="6">
              <date-select :filled="true" :closeOnContentClick="true" :value.sync="create.fields.at"></date-select>
            </v-col>
            <v-col cols="12" sm="6">
              <relation-select
                  :filled="true"
                  :entities="['monitors', 'websites', 'tags']"
                  :value.sync="create.fields.monitors_ways"
                  :closeOnContentClick="true">
              </relation-select>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                  :disabled="!create.valid"
                  :loading="create.loading"
                  color="success"
                  @click="createNote"
              >
                {{ $t('button-create-action') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-expand-transition>

      <v-text-field
          class="mb-4"
          dense
          rounded
          filled
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          :label="$t('actions.search-three-dots')"
          single-line
          hide-details
          append-icon="mdi-close"
          @click:append="search = ''"
      ></v-text-field>

      <v-data-table
          dense
          must-sort
          :items="table.items.all"
          :options.sync="table.options"
          sort-by="at"
          :sort-desc="true"
          :server-items-length="table.items.length"
          :loading="table.items.loading"
          :headers="table.headers"
          :show-select="$vuetify.breakpoint.mdAndUp"
          v-model="table.selected"
          itemid="id"
      >
        <template v-slot:item.description="{ item }">
          <v-edit-dialog
              @open="dialog.value = item.description"
              @save="$repos.notes.update(item.id, { description: dialog.value})"
              large
              :cancel-text="$t('actions.cancel')"
              :save-text="$t('actions.save')">
            <span class="editable">{{ item.description }}</span>
            <edit-dialog-pencil></edit-dialog-pencil>
            <template v-slot:input>
              <v-textarea
                  rows="3"
                  v-model="dialog.value"
                  :rules="create.rules.description"
                  :counter="1000"
                  auto-grow
              ></v-textarea>
            </template>
          </v-edit-dialog>
        </template>
        <template v-slot:item.at="{ item }">
          <v-edit-dialog
              @open="dialog.value = item.at"
              @save="$repos.notes.update(item.id, { at: dialog.value})"
              large
              :cancel-text="$t('actions.cancel')"
              :save-text="$t('actions.save')">
            <span class="editable">{{ $moment(item.at).format('ll') }}</span>
            <edit-dialog-pencil></edit-dialog-pencil>
            <template v-slot:input>
              <date-select :inline="true" :value.sync="dialog.value"></date-select>
            </template>
          </v-edit-dialog>
        </template>
        <template v-slot:item.monitors_ways="{ item }">
          <v-edit-dialog
              @open="dialog.value = item.monitors_ways"
              @save="$repos.notes.update(item.id, { monitors_ways: dialog.value})"
              large
              :cancel-text="$t('actions.cancel')"
              :save-text="$t('actions.save')">
            <span class="editable font-italic" v-if="item.monitors_ways.length">{{ item.monitors_ways.length }} {{ $t('fields.elements') }}</span>
            <span class="editable font-italic" v-else>{{ $t('fields.all') }}</span>
            <edit-dialog-pencil></edit-dialog-pencil>
            <template v-slot:input>
              <v-row style="margin: 0">
                <v-col>
                  <relation-select
                      :icon="false"
                      :entities="['monitors', 'websites', 'tags']"
                      :value.sync="dialog.value"
                      :closeOnContentClick="true">
                  </relation-select>
                </v-col>
              </v-row>
            </template>
          </v-edit-dialog>
        </template>
      </v-data-table>
    </div>
  </a-dialog>
</template>

<script>
import moment from 'moment'
import EditDialogPencil from '@/views/Common/Components/Table/EditDialogPencil'
import RelationSelect from '@/views/Common/Components/Selects/RelationSelect'
import DateSelect from '@/views/Common/Components/Selects/DateSelect'
import { debounce } from 'lodash'

export default {
  name: 'ManageNotesDialog',
  components: { DateSelect, RelationSelect, EditDialogPencil },
  data () {
    return {
      create: {
        opened: false,
        valid: false,
        loading: false,
        fields: {
          description: '',
          at: moment().format('YYYY-MM-DD'),
          monitors_ways: [],
        },
        rules: {
          description: [
            v => !!v || this.$t('description-required'),
            v => (v && v.length <= 1000) || this.$t('description-length'),
          ],
        }
      },
      dialog: {
        value: null
      },
      search: '',
      table: {
        options: {},
        items: this.$repos.notes.createDatatableCollection(),
        selected: [],
        headers: [
          { text: this.$t('date'), value: 'at' },
          { text: this.$t('description'), value: 'description' },
          { text: this.$t('link-to'), value: 'monitors_ways', sortable: false }
        ],
      },
      buttons: [
        {
          color: 'success',
          text: this.$t('button-create'),
          icon: 'mdi-plus',
          click: () => this.create.opened = !this.create.opened,
        },
        {
          color: 'error',
          confirmation: true,
          text: ({ selected }) => this.$t('actions.remove') + ' (' + selected.length + ')',
          icon: 'mdi-delete',
          disabled: ({ selected }) => selected.length === 0,
          click: ({ selected }) => this.removeNotes(selected.map(selected => selected.id)),
        }
      ]
    }
  },
  watch: {
    'table.options': {
      handler: async function () {
        await this.fetch()
      },
      deep: true
    },
    search: debounce(async function () {
      await this.fetch()
    }, 250)
  },
  computed: {
    createAtFormatted () {
      return moment(this.create.fields.at).format('ll')
    },
  },
  methods: {
    async fetch () {
      if (Object.keys(this.table.options).length > 0) {
        await this.table.items.fetch({
          ...this.table.options,
          search: this.search,
          with: ['monitors', 'websites', 'tags'],
        })
      }
    },
    close () {
      this.table.items.destroy()
      this.$reset()
    },
    handleTableSelect (ids) {
      this.table.selected = ids
    },
    async createNote () {
      try {
        this.create.loading = true
        this.table.selected = []

        await this.$repos.notes.create(this.create.fields)

        this.create.fields.description = ''

        this.$refs.form.resetValidation()
      } finally {
        this.create.loading = false
      }
    },
    async removeNotes (ids) {
      this.table.selected = []
      await this.$repos.notes.remove(ids)
    }
  }
}
</script>

<i18n>
{
	"en": {
		"button-create": "Create",
		"button-create-action": "Create new note",
		"date": "Dated",
		"description": "Description",
		"description-length": "The description of the note must contain a maximum of 1000 characters",
		"description-required": "Note description is required",
		"hint-description": "The description will be displayed in the evolution graph of your keywords.",
		"link-to": "Linked to",
		"manage": "Manage my notes",
		"required": "Note description is required"
	},
	"es": {
		"button-create": "Crear",
		"button-create-action": "Crear nueva nota",
		"date": "Fecha",
		"description": "Descripción",
		"description-length": "La descripción de la nota debe contener un máximo de 1000 caracteres",
		"description-required": "Se requiere una descripción de la nota",
		"hint-description": "La descripción se mostrará en el gráfico de evolución de sus palabras clave.",
		"link-to": "Vinculado a",
		"manage": "Gestionar mis notas",
		"required": "Se requiere una descripción de la nota"
	},
	"fr": {
		"button-create": "Ajouter",
		"button-create-action": "Ajouter une nouvelle note",
		"date": "Date",
		"description": "Description",
		"description-length": "La description de la note doit contenir maximum 1000 caractères",
		"description-required": "La description de la note est requise",
		"hint-description": "La description sera affichée dans le graphique d'évolution de vos mots clés.",
		"link-to": "Lié à",
		"manage": "Gérer mes notes",
		"required": "La description de la note est requise"
	},
	"it": {
		"button-create": "Crea",
		"button-create-action": "Crea nuova nota",
		"date": "Data",
		"description": "Descrizione",
		"description-length": "La descrizione della nota deve contenere al massimo 1000 caratteri",
		"description-required": "La descrizione della nota è obbligatoria",
		"hint-description": "La descrizione verrà visualizzata nel grafico di evoluzione delle parole chiave.",
		"link-to": "Collegato a",
		"manage": "Gestisci le mie note",
		"required": "La descrizione della nota è obbligatoria"
	},
	"pt": {
		"button-create": "Criar",
		"button-create-action": "Criar nova nota",
		"date": "Data",
		"description": "Descrição",
		"description-length": "A descrição da nota deve conter no máximo 1000 caracteres",
		"description-required": "A descrição da nota é obrigatória",
		"hint-description": "A descrição será exibida no gráfico de evolução das suas palavras-chave.",
		"link-to": "Associado a",
		"manage": "Gerir as minhas notas",
		"required": "A descrição da nota é obrigatória"
	},
	"pl": {
		"button-create": "Utwórz",
		"button-create-action": "Utwórz nową notatkę",
		"date": "Data",
		"description": "Opis",
		"description-length": "Opis notatki może zawierać maksymalnie 1000 znaków",
		"description-required": "Opis notatki jest wymagany",
		"hint-description": "Opis będzie wyświetlany na wykresie ewolucji Twoich słów kluczowych.",
		"link-to": "Powiązano z",
		"manage": "Zarządzaj moimi notatkami",
		"required": "Opis notatki jest wymagany"
	}
}
</i18n>
